import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, GraduationCap, Phone, Mail, ChevronRight } from "lucide-react";
import { collegeInfo } from "../mock/mock";

const navLinks = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Courses", to: "/courses" },
  { label: "Facilities", to: "/facilities" },
  { label: "Faculty", to: "/faculty" },
  { label: "Gallery", to: "/gallery" },
  { label: "Admissions", to: "/admissions" },
  { label: "Contact", to: "/contact" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header className="sticky top-0 z-50">
      {/* Top strip */}
      <div className="hidden md:block bg-brand-green-deep text-brand-ivory text-xs">
        <div className="max-w-7xl mx-auto px-6 py-2 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <a href={`tel:${collegeInfo.phone}`} className="flex items-center gap-2 hover:text-brand-gold transition-colors">
              <Phone className="w-3.5 h-3.5" />
              {collegeInfo.phone}
            </a>
            <a href={`mailto:${collegeInfo.email}`} className="flex items-center gap-2 hover:text-brand-gold transition-colors">
              <Mail className="w-3.5 h-3.5" />
              {collegeInfo.email}
            </a>
          </div>
          <div className="flex items-center gap-4">
            <span className="opacity-80">{collegeInfo.hours}</span>
            <span className="h-3 w-px bg-brand-gold/40" />
            <Link to="/admissions" className="text-brand-gold hover:text-white transition-colors font-medium">
              Admissions Open 2025 &rarr;
            </Link>
          </div>
        </div>
      </div>

      {/* Main bar */}
      <div
        className={`w-full transition-all duration-300 ${
          scrolled
            ? "bg-white/95 backdrop-blur-md shadow-[0_4px_20px_-8px_rgba(15,61,41,0.15)]"
            : "bg-white"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="relative">
              <div className="w-11 h-11 rounded-full bg-brand-green flex items-center justify-center border-2 border-brand-gold group-hover:rotate-6 transition-transform duration-500">
                <GraduationCap className="w-6 h-6 text-brand-gold" />
              </div>
              <div className="absolute -inset-1 rounded-full border border-brand-gold/40 group-hover:scale-110 transition-transform duration-500" />
            </div>
            <div className="leading-tight">
              <div className="font-serif-display text-[15px] md:text-base font-bold text-brand-green-deep">
                SHES D.Ed College
              </div>
              <div className="text-[10px] md:text-[11px] tracking-[0.18em] uppercase text-brand-gold-dark font-medium">
                Shre Hingulmabika Edu. Society &middot; Est. 2008
              </div>
            </div>
          </Link>

          {/* Desktop links */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => {
              const active = location.pathname === link.to;
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`relative px-3 py-2 text-sm font-medium tracking-wide transition-colors ${
                    active ? "text-brand-green-deep" : "text-neutral-700 hover:text-brand-green"
                  }`}
                >
                  {link.label}
                  <span
                    className={`absolute left-3 right-3 -bottom-0.5 h-[2px] bg-brand-gold origin-left transition-transform duration-300 ${
                      active ? "scale-x-100" : "scale-x-0"
                    }`}
                  />
                </Link>
              );
            })}
            <Link
              to="/admissions"
              className="ml-3 btn-gold px-5 py-2 rounded-full text-sm font-semibold flex items-center gap-1.5"
            >
              Apply Now <ChevronRight className="w-4 h-4" />
            </Link>
          </nav>

          {/* Mobile toggle */}
          <button
            onClick={() => setOpen(!open)}
            className="lg:hidden w-10 h-10 rounded-md flex items-center justify-center text-brand-green hover:bg-brand-ivory transition-colors"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile menu */}
        <div
          className={`lg:hidden overflow-hidden transition-[max-height] duration-500 ease-in-out ${
            open ? "max-h-[600px]" : "max-h-0"
          }`}
        >
          <div className="px-6 py-4 bg-white border-t border-brand-ivory">
            <div className="flex flex-col gap-1">
              {navLinks.map((link) => {
                const active = location.pathname === link.to;
                return (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`py-3 px-3 rounded-md text-sm font-medium flex items-center justify-between ${
                      active
                        ? "bg-brand-ivory text-brand-green-deep"
                        : "text-neutral-700 hover:bg-brand-ivory/60"
                    }`}
                  >
                    {link.label}
                    <ChevronRight className="w-4 h-4 opacity-40" />
                  </Link>
                );
              })}
              <Link
                to="/admissions"
                className="btn-gold mt-3 px-5 py-3 rounded-md text-sm font-semibold text-center"
              >
                Apply Now
              </Link>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
